import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ProductPrice } from "src/api/types";
import { usePrices } from "src/context/PricesProvider";
import { useUpdateVisitMetadata } from "./useUpdateVisitMetadata";

export const useSelectedProduct = (): [
  ProductPrice | undefined,
  (product: ProductPrice) => void,
] => {
  const products = usePrices();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramKey = "price";
  const { update } = useUpdateVisitMetadata();

  const fallback = useMemo(() => {
    return (
      products.find((p) => p.default_selection) ??
      products.find((p) => p.number_of_months === 1) ??
      products[0]
    );
  }, [products]);

  const selectedFromUrl = useMemo(() => {
    const v = searchParams.get(paramKey);
    if (!v) return undefined;

    return products.find((p) => p.stripe_id === v);
  }, [paramKey, products, searchParams]);

  const selectedProduct = selectedFromUrl ?? fallback;

  const setProduct = useCallback(
    (product: ProductPrice) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(paramKey, product.stripe_id);
        return next;
      });

      update({
        price: `${product.name} - $${product.amount}`,
      });
    },
    [setSearchParams, update],
  );

  useEffect(() => {
    if (!selectedProduct) return;

    const hasParam = searchParams.get(paramKey) != null;
    if (hasParam) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(paramKey, selectedProduct.stripe_id);
        return next;
      },
      { replace: true },
    );

    update({
      price: `${selectedProduct.name} - $${selectedProduct.amount}`,
    });
  }, [paramKey, searchParams, selectedProduct, setSearchParams, update]);

  return [selectedProduct, setProduct];
};
